import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Virtual Scrolling Hook für große Listen
export const useVirtualScroll = (items, itemHeight = 60, containerHeight = 400) => {
    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef(null);

    const visibleItems = useMemo(() => {
        const startIndex = Math.floor(scrollTop / itemHeight);
        const endIndex = Math.min(
            startIndex + Math.ceil(containerHeight / itemHeight) + 1,
            items.length
        );

        return items.slice(startIndex, endIndex).map((item, index) => ({
            item,
            index: startIndex + index,
            top: (startIndex + index) * itemHeight
        }));
    }, [items, scrollTop, itemHeight, containerHeight]);

    const totalHeight = items.length * itemHeight;

    const handleScroll = (e) => {
        setScrollTop(e.target.scrollTop);
    };

    return {
        visibleItems,
        totalHeight,
        containerRef,
        handleScroll,
        containerStyle: {
            height: containerHeight,
            overflow: 'auto'
        },
        contentStyle: {
            height: totalHeight,
            position: 'relative'
        }
    };
};

// Memory Leak Prevention Hook
export const useMemoryCleanup = () => {
    const timersRef = useRef(new Set());
    const intervalsRef = useRef(new Set());
    const listenersRef = useRef(new Set());

    const addTimer = (callback, delay) => {
        const timer = setTimeout(callback, delay);
        timersRef.current.add(timer);
        return timer;
    };

    const addInterval = (callback, delay) => {
        const interval = setInterval(callback, delay);
        intervalsRef.current.add(interval);
        return interval;
    };

    const addListener = (element, event, handler) => {
        element.addEventListener(event, handler);
        listenersRef.current.add({ element, event, handler });
    };

    useEffect(() => {
        return () => {
            // Cleanup all timers
            timersRef.current.forEach(timer => clearTimeout(timer));
            intervalsRef.current.forEach(interval => clearInterval(interval));
            
            // Cleanup all listeners
            listenersRef.current.forEach(({ element, event, handler }) => {
                element.removeEventListener(event, handler);
            });

            // Clear all sets
            timersRef.current.clear();
            intervalsRef.current.clear();
            listenersRef.current.clear();
        };
    }, []);

    return { addTimer, addInterval, addListener };
};

// Component Lifecycle Manager
export const useComponentLifecycle = (componentName) => {
    const mountTime = useRef(Date.now());
    const renderCount = useRef(0);

    useEffect(() => {
        renderCount.current += 1;
        
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[Lifecycle] ${componentName} render #${renderCount.current}`);
        }
    });

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[Lifecycle] ${componentName} mounted`);
        }

        return () => {
            const lifetime = Date.now() - mountTime.current;
            if (process.env.NODE_ENV !== 'production') {
                console.log(`[Lifecycle] ${componentName} unmounted after ${lifetime}ms (${renderCount.current} renders)`);
            }
        };
    }, [componentName]);
};

// Image Lazy Loading Hook
export const useLazyImage = (src, placeholder = null) => {
    const [imageSrc, setImageSrc] = useState(placeholder);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        if (!src) return;

        const img = new Image();
        img.src = src;

        img.onload = () => {
            setImageSrc(src);
            setIsLoaded(true);
            setError(null);
        };

        img.onerror = () => {
            setError(new Error(`Failed to load image: ${src}`));
            setIsLoaded(false);
        };

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [src, placeholder]);

    return { imageSrc, isLoaded, error, imgRef };
};

export default {
    useVirtualScroll,
    useMemoryCleanup,
    useComponentLifecycle,
    useLazyImage
};
